import React, { useEffect, useState } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import styles from './AISettings.module.css';
import Loading from '../../../../components/loading/Loading';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../../components/buttons/CustomButton';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { USER_ROLES } from '../../../../constants/userRoles';
import {
  Alert,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

const EMPTY_FORM = {
  assistant_name: '',
  use_emojis: true,
  suggest_slots: true,
  tone: 'neutro',
  response_length: 'media',
  communication_instructions: '',
  knowledge: '',
};

/**
 * Configuração da atendente virtual (AI Patient Response Engine — Fase 2).
 * Controla identidade, estilo de comunicação e a base de conhecimento
 * usada pela IA nas respostas aos pacientes.
 */
function AISettings() {
  const { GetAISettings, SetAISettings } = DataProviderInstance();
  const { hasRole } = useRoleAccess();
  const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    GetAISettings((result) => {
      setLoading(false);
      if (result?.success && result.settings) {
        setForm({ ...EMPTY_FORM, ...result.settings });
      } else if (!result?.success) {
        setErrorMsg('Erro ao carregar as configurações da IA.');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = () => {
    setSaving(true);
    setSuccessMsg('');
    setErrorMsg('');
    SetAISettings(form, (result) => {
      setSaving(false);
      if (result?.success) {
        setSuccessMsg('Configurações salvas com sucesso. As próximas respostas da IA já usarão essas preferências.');
      } else {
        setErrorMsg(result?.error || 'Erro ao salvar as configurações da IA.');
      }
    });
  };

  if (loading) {
    return (
      <div className='page-content'>
        <Loading />
      </div>
    );
  }

  return (
    <div className='page-content'>
      <div className={styles.pageHeader}>
        <AreaTitle
          Title='Assistente de IA'
          Icon={<SmartToyOutlinedIcon />}
          Description='Configure a identidade, o estilo de comunicação e a base de conhecimento da atendente virtual. Essas preferências valem para o Simulador de Atendimento e para a sugestão de resposta no chat de WhatsApp.'
        />
      </div>

      <Paper className={styles.paper} elevation={0} variant='outlined'>
        {!canEdit && (
          <Alert severity='info' style={{ marginBottom: '16px' }}>
            Apenas administradores podem alterar essas configurações.
          </Alert>
        )}
        {successMsg && <Alert severity='success' style={{ marginBottom: '16px' }}>{successMsg}</Alert>}
        {errorMsg && <Alert severity='error' style={{ marginBottom: '16px' }}>{errorMsg}</Alert>}

        <div className={styles.section}>
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>Identidade</Typography>
          <TextField
            label='Nome da atendente virtual'
            placeholder='Ex: Gabriela'
            value={form.assistant_name}
            onChange={handleChange('assistant_name')}
            disabled={!canEdit}
            helperText='Como a IA se apresenta aos pacientes. Deixe vazio para se apresentar apenas como "secretária da clínica".'
            fullWidth
          />
        </div>

        <Divider style={{ margin: '20px 0' }} />

        <div className={styles.section}>
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>Estilo das respostas</Typography>
          <div className={styles.row}>
            <FormControl fullWidth>
              <InputLabel id='ai-tone-label'>Tom de comunicação</InputLabel>
              <Select
                labelId='ai-tone-label'
                label='Tom de comunicação'
                value={form.tone}
                onChange={handleChange('tone')}
                disabled={!canEdit}
              >
                <MenuItem value='informal'>Informal e próximo</MenuItem>
                <MenuItem value='neutro'>Neutro (cordial e profissional)</MenuItem>
                <MenuItem value='formal'>Formal</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id='ai-length-label'>Tamanho das respostas</InputLabel>
              <Select
                labelId='ai-length-label'
                label='Tamanho das respostas'
                value={form.response_length}
                onChange={handleChange('response_length')}
                disabled={!canEdit}
              >
                <MenuItem value='curta'>Curtas e diretas</MenuItem>
                <MenuItem value='media'>Médias</MenuItem>
                <MenuItem value='longa'>Completas e detalhadas</MenuItem>
              </Select>
            </FormControl>
          </div>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.use_emojis}
                onChange={(e) => setForm((prev) => ({ ...prev, use_emojis: e.target.checked }))}
                disabled={!canEdit}
              />
            }
            label='Permitir emojis nas respostas'
          />
          <TextField
            label='Instruções de comunicação (opcional)'
            placeholder={'Ex: Sempre confirmar o nome do paciente no início.\nNunca falar de valores antes de entender a queixa.\nEvitar a palavra "procedimento", preferir "consulta".'}
            value={form.communication_instructions}
            onChange={handleChange('communication_instructions')}
            disabled={!canEdit}
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            helperText='Regras livres sobre como a IA deve se comunicar. Têm prioridade sobre as opções acima.'
          />
        </div>

        <Divider style={{ margin: '20px 0' }} />

        <div className={styles.section}>
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>Agenda</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.suggest_slots}
                onChange={(e) => setForm((prev) => ({ ...prev, suggest_slots: e.target.checked }))}
                disabled={!canEdit}
              />
            }
            label='Sugerir horários livres da agenda'
          />
          <Typography variant='body2' color='text.secondary'>
            Ligado, a IA consulta a agenda na hora e oferece 2 ou 3 horários realmente livres, sem reservar. Desligado, ela pergunta a preferência da pessoa e diz que a equipe confirma.
          </Typography>
        </div>

        <Divider style={{ margin: '20px 0' }} />

        <div className={styles.section}>
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>Base de conhecimento</Typography>
          <TextField
            label='Perguntas frequentes, políticas e informações da clínica'
            placeholder={'Cole aqui perguntas e respostas, horários, convênios, políticas... (pode copiar direto de uma planilha)\n\nEx:\nVocês aceitam convênio? Não atendemos por convênio, mas emitimos recibo e nota fiscal para reembolso.\nHorário de atendimento: segunda a sexta, das 8h às 19h.'}
            value={form.knowledge}
            onChange={handleChange('knowledge')}
            disabled={!canEdit}
            multiline
            minRows={8}
            maxRows={24}
            fullWidth
            helperText='Fonte oficial de informação da IA, com prioridade sobre os dados do cadastro em caso de conflito.'
          />
        </div>

        {canEdit && (
          <div className={styles.actions}>
            <CustomButton
              variant='contained'
              style='primary'
              label='Salvar configurações'
              icon={<SaveOutlinedIcon />}
              onClick={handleSave}
              enabled={!saving}
              loading={saving}
            />
          </div>
        )}
      </Paper>
    </div>
  );
}

export default AISettings;
